/**
 * Multi-greenhouse mock store (up to MAX_HOUSES).
 * Future: REST/WS per house id → ESP32 / cloud. Never pretend live in v0.
 * Hard interlocks (E-stop, RCD, typhoon field unlock, breaker) are status-only.
 */

const HOUR = 3600 * 1000;
export const MAX_HOUSES = 1000;
const LS_KEY = 'ghc-houses-meta-v1';

const MODES = [
  { id: 'normal', label: '正常', labelEn: 'Normal' },
  { id: 'away', label: '外出', labelEn: 'Away' },
  { id: 'season', label: '季節', labelEn: 'Seasonal' },
  { id: 'rain', label: '雨天', labelEn: 'Rain' },
  { id: 'typhoon', label: '颱風', labelEn: 'Typhoon' },
];

const PLANT_PROFILES = [
  { id: 'leafy', name: '葉菜', nameEn: 'Leafy greens', temp: [18, 26], humidity: [50, 75] },
  { id: 'tomato', name: '番茄', nameEn: 'Tomato', temp: [20, 28], humidity: [55, 70] },
  { id: 'herb', name: '香草', nameEn: 'Herbs', temp: [16, 24], humidity: [45, 65] },
];

const ROLES = [
  { id: 'owner', label: 'Owner 東主', canControl: true },
  { id: 'operator', label: 'Operator 操作員', canControl: true },
  { id: 'viewer', label: 'Viewer 唯讀', canControl: false },
];

function makeWindow(id, name) {
  return {
    id, name,
    position: 0,
    moving: false,
    atLimitOpen: false,
    atLimitClose: true,
    fault: null,
    overrideUntil: null,
  };
}

function nowIso() {
  return new Date().toISOString();
}

function defaultCameras(code) {
  return [
    {
      id: 'cam1',
      name: 'Cam 1 · 東門',
      nameEn: 'Cam 1 · East door',
      online: false,
      demo: true,
      note: '示範鏡頭 · 未連線（獨立 NVR，App 只顯示畫面／警報聯動）',
    },
    {
      id: 'cam2',
      name: 'Cam 2 · 通道',
      nameEn: 'Cam 2 · Aisle',
      online: false,
      demo: true,
      note: '預留第二路 · 未接',
    },
  ];
}

/** Full per-house runtime state (lazy-created; only active house ticked heavily). */
export function createHouseState({ id, code, name, modelUrl } = {}) {
  const codeStr = code || id || 'house';
  const state = {
    houseId: id,
    houseCode: codeStr,
    houseName: name || codeStr,
    modelUrl: modelUrl || '/models/demo-l128.glb',
    connected: false,
    demoMode: true,
    mode: 'normal',
    controlMode: 'auto',
    manualHoldUntil: null,
    defaultHoldMs: 3 * HOUR,
    typhoonHoldMs: 30 * 60 * 1000,

    power: {
      mains: 'ok',
      ups: 'ok',
      comms: 'offline',
    },

    hardwareLocks: {
      eStop: false,
      rcdTrip: false,
      overload: false,
      typhoonFieldLock: false,
      watchdogSafe: false,
    },

    env: {
      indoorTemp: [24.2, 25.1, 23.8],
      outdoorTemp: 28.4,
      indoorHumidity: [62, 64, 61],
      outdoorHumidity: 78,
      lightLux: 42000,
      co2Ppm: 680,
      rainMmH: 0,
      windMs: 2.1,
      history: {
        temp: Array.from({ length: 24 }, (_, i) => 22 + Math.sin(i / 3) * 3 + Math.random()),
        humidity: Array.from({ length: 24 }, (_, i) => 55 + Math.cos(i / 4) * 8 + Math.random() * 2),
        co2: Array.from({ length: 24 }, (_, i) => 600 + Math.sin(i / 5) * 80 + Math.random() * 20),
      },
    },

    windows: [
      makeWindow('w1', '窗 1（南外）'),
      makeWindow('w2', '窗 2（南內）'),
      makeWindow('w3', '窗 3（北外）'),
      makeWindow('w4', '窗 4（北內）'),
    ],
    curtain: {
      id: 'curtain', name: '屋頂簾',
      position: 100,
      moving: false,
      atLimitOpen: false,
      atLimitClose: false,
      fault: null,
      overrideUntil: null,
    },

    lights: {
      whiteWalkway: { on: false, fault: null, name: '白光行人燈', circuit: 'white-walkway' },
      redGrow: { on: false, fault: null, name: '紅光補光（三層）', circuit: 'red-grow' },
    },

    fans: {
      exhaust: { on: false, fault: null, name: '排風／抽氣扇' },
      circulate: { on: false, fault: null, name: '循環扇' },
    },
    ac: { enabled: false, fault: null, name: '冷氣使能' },

    water: {
      tankLevelPct: 72,
      lowLevelThreshold: 15,
      irrigationPump: { on: false, fault: null, name: '灌溉泵' },
      streetValve: { on: false, fault: null, name: '街水閥' },
      fertPump: { on: false, fault: null, name: '肥泵', timerMin: 0 },
      fertigation: {
        ecTarget: 1.8,
        ecMax: 3.2,
        microbialCycleOn: false,
        microbialMaxHours: 6,
        microbialHoursRun: 0,
      },
    },

    cameras: defaultCameras(codeStr),

    plantProfileId: 'leafy',
    alarms: [
      {
        id: 'a1', sev: 'info', code: 'DEMO',
        msg: '示範模式・未連線真棚', msgEn: 'Demo mode · not linked to field',
        ts: nowIso(), cleared: false,
      },
    ],
    overrideLog: [],
    selectedHotspot: null,
    _tick: 0,
  };
  // Demo status pins for l128 so Sam sees 綠／紅／✕ without coding
  if (String(codeStr).toLowerCase() === 'l128' || id === 'l128-pilot') {
    applyL128DemoPinStatus(state);
  }
  return state;
}

function applyL128DemoPinStatus(state) {
  const w1 = state.windows.find((w) => w.id === 'w1');
  if (w1) {
    w1.position = 60;
    w1.atLimitClose = false;
    w1.atLimitOpen = false;
    w1.fault = null;
  }
  const w3 = state.windows.find((w) => w.id === 'w3');
  if (w3) {
    w3.fault = '過流';
    w3.position = 0;
    w3.atLimitClose = true;
    w3.atLimitOpen = false;
  }
  state.lights.redGrow.on = true;
  state.fans.circulate.on = true;
}

/** Lightweight tab row — never hold 1000 full DOM states in UI from this alone. */
function metaOf(h) {
  return { id: h.houseId, code: h.houseCode, name: h.houseName };
}

function seedDefaults() {
  const list = [
    createHouseState({
      id: 'l128-pilot',
      code: 'l128',
      name: 'Co-Make l128 試點棚',
    }),
    createHouseState({
      id: 'demo-2',
      code: '#2',
      name: '示範棚 #2',
    }),
    createHouseState({
      id: 'demo-3',
      code: '#3',
      name: '示範棚 #3',
    }),
  ];
  // Slight env offset so stubs look distinct
  list[1].env.indoorTemp = [22.0, 22.4, 21.8];
  list[1].water.tankLevelPct = 55;
  list[2].env.indoorTemp = [26.1, 26.5, 25.9];
  list[2].mode = 'away';

  return list;
}

function loadPersistedMeta() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.houseMeta) || !parsed.houseMeta.length) return null;
    return parsed;
  } catch {
    return null;
  }
}

function persistMeta() {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify({
      activeHouseId,
      houseMeta: houseMeta.slice(0, MAX_HOUSES),
    }));
  } catch {
    /* quota / private mode */
  }
}

/** @type {Record<string, object>} */
const houses = Object.create(null);
/** @type {{id:string,code:string,name:string}[]} */
let houseMeta = [];
let activeHouseId = 'l128-pilot';
let role = 'owner';
let lang = 'zh-Hant';

(function init() {
  const saved = typeof localStorage !== 'undefined' ? loadPersistedMeta() : null;
  if (saved && saved.houseMeta.length) {
    for (const m of saved.houseMeta.slice(0, MAX_HOUSES)) {
      const h = createHouseState({ id: m.id, code: m.code, name: m.name });
      houses[h.houseId] = h;
      houseMeta.push(metaOf(h));
    }
    activeHouseId = houses[saved.activeHouseId] ? saved.activeHouseId : houseMeta[0].id;
  } else {
    for (const h of seedDefaults()) {
      houses[h.houseId] = h;
      houseMeta.push(metaOf(h));
    }
    activeHouseId = 'l128-pilot';
  }
})();

const listeners = new Set();

function active() {
  return houses[activeHouseId];
}

function emit() {
  for (const fn of listeners) fn(getState());
}

/**
 * Public snapshot: active house fields + lightweight house list (not all house bodies).
 */
export function getState() {
  const h = active();
  return {
    ...structuredClone(h),
    role,
    lang,
    activeHouseId,
    houseList: houseMeta.map((m) => ({ ...m })),
    houseCount: houseMeta.length,
    maxHouses: MAX_HOUSES,
  };
}

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function getModes() { return MODES; }
export function getPlantProfiles() { return PLANT_PROFILES; }
export function getRoles() { return ROLES; }
export function getHouseList() { return houseMeta.map((m) => ({ ...m })); }
export function getMaxHouses() { return MAX_HOUSES; }

export function setActiveHouse(id) {
  if (!houses[id]) return { ok: false, reason: 'missing' };
  if (activeHouseId === id) return { ok: true, same: true };
  activeHouseId = id;
  persistMeta();
  emit();
  return { ok: true };
}

/**
 * Add a mock house. Caps at MAX_HOUSES. State is created lazily-full but only one is active.
 */
export function addHouse({ code, name } = {}) {
  if (houseMeta.length >= MAX_HOUSES) {
    return { ok: false, reason: 'max_houses', max: MAX_HOUSES };
  }
  const n = houseMeta.length + 1;
  const id = `house-${Date.now().toString(36)}-${n}`;
  const codeStr = (code && String(code).trim()) || `#${n}`;
  const nameStr = (name && String(name).trim()) || `溫室 ${codeStr}`;
  if (houseMeta.some((m) => m.code === codeStr)) {
    return { ok: false, reason: 'duplicate_code' };
  }
  const h = createHouseState({ id, code: codeStr, name: nameStr });
  houses[id] = h;
  houseMeta.push(metaOf(h));
  activeHouseId = id;
  persistMeta();
  emit();
  return { ok: true, id, code: codeStr };
}

/** Ensure house state exists for id (for future lazy stubs). */
export function ensureHouse(id) {
  if (houses[id]) return houses[id];
  const m = houseMeta.find((x) => x.id === id);
  if (!m) return null;
  const h = createHouseState({ id: m.id, code: m.code, name: m.name });
  houses[id] = h;
  return h;
}

function canControl() {
  const r = ROLES.find((x) => x.id === role);
  return !!(r && r.canControl);
}

function logOverride(action, detail = {}) {
  const state = active();
  state.overrideLog.unshift({
    ts: nowIso(),
    action,
    detail,
    role,
    mode: state.mode,
    houseId: state.houseId,
  });
  if (state.overrideLog.length > 80) state.overrideLog.length = 80;
}

function holdMs() {
  const state = active();
  return state.mode === 'typhoon' ? state.typhoonHoldMs : state.defaultHoldMs;
}

function startManualHold() {
  const state = active();
  state.controlMode = 'manual';
  state.manualHoldUntil = Date.now() + holdMs();
}

function isTyphoonLocked() {
  const state = active();
  return state.mode === 'typhoon' || state.hardwareLocks.typhoonFieldLock;
}

function isLowWater() {
  const state = active();
  return state.water.tankLevelPct < state.water.lowLevelThreshold;
}

function windAlarmActive() {
  const state = active();
  return state.env.windMs >= 12 || state.alarms.some((a) => !a.cleared && a.code === 'WIND');
}

function hardwareBlocked() {
  const h = active().hardwareLocks;
  return h.eStop || h.rcdTrip || h.overload || h.watchdogSafe;
}

export function setRole(roleId) {
  if (!ROLES.some((r) => r.id === roleId)) return;
  role = roleId;
  emit();
}

export function setLang(langId) {
  lang = langId;
  emit();
}

export function setPlantProfile(id) {
  if (!canControl()) return { ok: false, reason: 'viewer_readonly' };
  if (!PLANT_PROFILES.some((p) => p.id === id)) return { ok: false, reason: 'unknown' };
  active().plantProfileId = id;
  logOverride('plant_profile', { id });
  emit();
  return { ok: true };
}

export function setMode(modeId) {
  if (!canControl()) return { ok: false, reason: 'viewer_readonly' };
  if (!MODES.some((m) => m.id === modeId)) return { ok: false, reason: 'unknown' };
  const state = active();
  const prev = state.mode;
  state.mode = modeId;
  if (modeId === 'typhoon') {
    for (const w of state.windows) {
      w.position = 0;
      w.moving = false;
      w.atLimitClose = true;
      w.atLimitOpen = false;
    }
    state.curtain.position = 50;
    state.curtain.moving = false;
    state.fans.exhaust.on = false;
    state.fans.circulate.on = false;
    state.lights.whiteWalkway.on = false;
    state.lights.redGrow.on = false;
    state.ac.enabled = false;
    state.water.irrigationPump.on = false;
    state.water.fertPump.on = false;
    state.water.streetValve.on = false;
    addAlarm('crit', 'TYPHOON', '已進入颱風模式：窗簾安全位，風扇／補光／肥水／冷氣已停。App 不可遠端強開窗。', 'Typhoon mode: safe positions; App cannot force-open windows.');
  }
  logOverride('set_mode', { from: prev, to: modeId });
  emit();
  return { ok: true };
}

export function addAlarm(sev, code, msg, msgEn) {
  const state = active();
  state.alarms.unshift({
    id: `a${Date.now()}`, sev, code, msg, msgEn: msgEn || msg,
    ts: nowIso(), cleared: false,
  });
}

export function clearAlarm(id) {
  if (!canControl()) return { ok: false, reason: 'viewer_readonly' };
  const a = active().alarms.find((x) => x.id === id);
  if (!a) return { ok: false, reason: 'missing' };
  if (['ESTOP', 'RCD', 'OVERLOAD', 'TYPHOON_FIELD'].includes(a.code)) {
    return { ok: false, reason: 'hardware_only' };
  }
  a.cleared = true;
  logOverride('clear_alarm', { id, code: a.code });
  emit();
  return { ok: true };
}

function windowMoveBlocked(reasonOut) {
  if (!canControl()) { reasonOut.reason = 'viewer_readonly'; return true; }
  if (hardwareBlocked()) { reasonOut.reason = 'hardware_lock'; return true; }
  if (isTyphoonLocked()) { reasonOut.reason = 'typhoon_lock'; return true; }
  return false;
}

export function setWindowPosition(windowId, positionPct) {
  const reasonOut = {};
  if (windowMoveBlocked(reasonOut)) return { ok: false, ...reasonOut };
  const w = active().windows.find((x) => x.id === windowId);
  if (!w) return { ok: false, reason: 'missing' };
  if (w.fault) return { ok: false, reason: 'actuator_fault', fault: w.fault };

  const target = Math.max(0, Math.min(100, Number(positionPct)));
  if (windAlarmActive() && target > w.position) {
    return { ok: false, reason: 'wind_alarm_lock' };
  }
  w.position = target;
  w.moving = false;
  w.atLimitOpen = target >= 100;
  w.atLimitClose = target <= 0;
  startManualHold();
  logOverride('window_set', { id: windowId, position: target });
  emit();
  return { ok: true };
}

export function stopWindow(windowId) {
  if (!canControl()) return { ok: false, reason: 'viewer_readonly' };
  const w = active().windows.find((x) => x.id === windowId);
  if (!w) return { ok: false, reason: 'missing' };
  w.moving = false;
  startManualHold();
  logOverride('window_stop', { id: windowId, position: w.position });
  emit();
  return { ok: true };
}

export function setAllWindows(positionPct) {
  const reasonOut = {};
  if (windowMoveBlocked(reasonOut)) return { ok: false, ...reasonOut };
  const target = Math.max(0, Math.min(100, Number(positionPct)));
  if (windAlarmActive() && target > 0) {
    return { ok: false, reason: 'wind_alarm_no_force_open' };
  }
  for (const w of active().windows) {
    if (w.fault) continue;
    w.position = target;
    w.moving = false;
    w.atLimitOpen = target >= 100;
    w.atLimitClose = target <= 0;
  }
  startManualHold();
  logOverride('windows_all', { position: target });
  emit();
  return { ok: true };
}

export function setCurtainPosition(positionPct) {
  const reasonOut = {};
  if (windowMoveBlocked(reasonOut)) return { ok: false, ...reasonOut };
  const c = active().curtain;
  if (c.fault) return { ok: false, reason: 'actuator_fault', fault: c.fault };
  const target = Math.max(0, Math.min(100, Number(positionPct)));
  if (windAlarmActive() && target < 40) {
    return { ok: false, reason: 'wind_alarm_curtain_safe' };
  }
  c.position = target;
  c.moving = false;
  c.atLimitOpen = target >= 100;
  c.atLimitClose = target <= 0;
  startManualHold();
  logOverride('curtain_set', { position: target });
  emit();
  return { ok: true };
}

export function stopCurtain() {
  if (!canControl()) return { ok: false, reason: 'viewer_readonly' };
  active().curtain.moving = false;
  startManualHold();
  logOverride('curtain_stop', { position: active().curtain.position });
  emit();
  return { ok: true };
}

export function setLight(circuit, on) {
  if (!canControl()) return { ok: false, reason: 'viewer_readonly' };
  if (hardwareBlocked()) return { ok: false, reason: 'hardware_lock' };
  if (active().mode === 'typhoon') return { ok: false, reason: 'typhoon_lock' };
  if (circuit === 'white') active().lights.whiteWalkway.on = !!on;
  else if (circuit === 'red') active().lights.redGrow.on = !!on;
  else return { ok: false, reason: 'unknown_circuit' };
  startManualHold();
  logOverride('light', { circuit, on: !!on });
  emit();
  return { ok: true };
}

export function setFan(which, on) {
  if (!canControl()) return { ok: false, reason: 'viewer_readonly' };
  if (hardwareBlocked()) return { ok: false, reason: 'hardware_lock' };
  if (active().mode === 'typhoon') return { ok: false, reason: 'typhoon_lock' };
  if (which === 'exhaust') {
    if (on && active().ac.enabled) {
      return { ok: false, reason: 'ac_interlock', detail: '冷氣使能開啟時排風強制關閉' };
    }
    active().fans.exhaust.on = !!on;
  } else if (which === 'circulate') {
    active().fans.circulate.on = !!on;
  } else return { ok: false, reason: 'unknown' };
  startManualHold();
  logOverride('fan', { which, on: !!on });
  emit();
  return { ok: true };
}

export function setAcEnabled(on) {
  if (!canControl()) return { ok: false, reason: 'viewer_readonly' };
  if (hardwareBlocked()) return { ok: false, reason: 'hardware_lock' };
  if (active().mode === 'typhoon') return { ok: false, reason: 'typhoon_lock' };
  active().ac.enabled = !!on;
  if (on) active().fans.exhaust.on = false;
  startManualHold();
  logOverride('ac', { enabled: !!on, exhaustForcedOff: !!on });
  emit();
  return { ok: true };
}

export function setPump(which, on) {
  if (!canControl()) return { ok: false, reason: 'viewer_readonly' };
  if (hardwareBlocked()) return { ok: false, reason: 'hardware_lock' };
  if (active().mode === 'typhoon') return { ok: false, reason: 'typhoon_lock' };
  if (isLowWater() && (which === 'irrigation' || which === 'fert')) {
    return { ok: false, reason: 'low_water' };
  }
  if (which === 'irrigation') active().water.irrigationPump.on = !!on;
  else if (which === 'fert') active().water.fertPump.on = !!on;
  else if (which === 'street') active().water.streetValve.on = !!on;
  else return { ok: false, reason: 'unknown' };
  startManualHold();
  logOverride('pump', { which, on: !!on });
  emit();
  return { ok: true };
}

export function setFertigation({ ecTarget, microbialCycleOn } = {}) {
  if (!canControl()) return { ok: false, reason: 'viewer_readonly' };
  const f = active().water.fertigation;
  if (ecTarget != null) {
    const v = Number(ecTarget);
    if (Number.isNaN(v) || v < 0.5 || v > f.ecMax) {
      return { ok: false, reason: 'ec_limit', max: f.ecMax };
    }
    f.ecTarget = v;
  }
  if (microbialCycleOn != null) {
    if (microbialCycleOn && f.microbialHoursRun >= f.microbialMaxHours) {
      return { ok: false, reason: 'microbial_limit', max: f.microbialMaxHours };
    }
    f.microbialCycleOn = !!microbialCycleOn;
  }
  startManualHold();
  logOverride('fertigation', { ecTarget: f.ecTarget, microbial: f.microbialCycleOn });
  emit();
  return { ok: true };
}

export function setSelectedHotspot(id) {
  active().selectedHotspot = id;
  emit();
}

export function simulateHardware({ eStop, rcdTrip, overload, typhoonFieldLock, lowWater, windMs, co2High } = {}) {
  const state = active();
  if (eStop != null) state.hardwareLocks.eStop = !!eStop;
  if (rcdTrip != null) state.hardwareLocks.rcdTrip = !!rcdTrip;
  if (overload != null) state.hardwareLocks.overload = !!overload;
  if (typhoonFieldLock != null) state.hardwareLocks.typhoonFieldLock = !!typhoonFieldLock;
  if (lowWater) state.water.tankLevelPct = 10;
  if (windMs != null) state.env.windMs = windMs;
  if (co2High) {
    state.env.co2Ppm = 1850;
    addAlarm('warn', 'CO2', 'CO₂ 偏高（只警報，無增補閥）', 'CO₂ high — alarm only, no inject');
  }
  emit();
}

/** Tick only the active house (scale: do not animate 1000 houses every second). */
export function tick() {
  const state = active();
  if (!state) return;
  state._tick += 1;
  const e = state.env;
  e.indoorTemp = e.indoorTemp.map((t) => +(t + (Math.random() - 0.5) * 0.08).toFixed(1));
  e.outdoorTemp = +(e.outdoorTemp + (Math.random() - 0.5) * 0.05).toFixed(1);
  e.indoorHumidity = e.indoorHumidity.map((h) => +Math.min(95, Math.max(30, h + (Math.random() - 0.5) * 0.2)).toFixed(0));
  e.co2Ppm = Math.round(e.co2Ppm + (Math.random() - 0.5) * 4);
  if (e.co2Ppm > 1500 && !state.alarms.some((a) => !a.cleared && a.code === 'CO2')) {
    addAlarm('warn', 'CO2', 'CO₂ 偏高（只警報，無增補閥）', 'CO₂ high — alarm only, no inject');
  }

  if (state.controlMode === 'manual' && state.manualHoldUntil && Date.now() > state.manualHoldUntil) {
    state.controlMode = 'auto';
    state.manualHoldUntil = null;
    logOverride('auto_resume', { reason: 'hold_timeout' });
  }

  if (state.water.fertigation.microbialCycleOn) {
    state.water.fertigation.microbialHoursRun += 1 / 3600;
    if (state.water.fertigation.microbialHoursRun >= state.water.fertigation.microbialMaxHours) {
      state.water.fertigation.microbialCycleOn = false;
      addAlarm('info', 'MICRO', '微生物循環已達上限時數，已自動停止', 'Microbial cycle hit max hours');
    }
  }

  if (state.ac.enabled) state.fans.exhaust.on = false;

  emit();
}

export function reasonMessage(code, langArg = lang) {
  const zh = {
    viewer_readonly: 'Viewer 唯讀：只可睇 3D／狀態，唔可以控制。',
    hardware_lock: '硬件互鎖（急停／漏電／過載／看門狗）— App 唔可以遠端解除。',
    typhoon_lock: '颱風／風暴鎖窗：只可以由現場解鎖；App 唔可以強開窗。',
    wind_alarm_lock: '風速警報中：唔可以再開窗。',
    wind_alarm_no_force_open: '風速警報中：禁止一鍵強開全部窗簾。',
    wind_alarm_curtain_safe: '風速警報中：簾要保持安全位。',
    low_water: '缸水位過低：泵已禁（硬件亦會禁）。',
    ac_interlock: '冷氣使能開啟 → 排風強制關閉。',
    actuator_fault: '執行器故障（限位／過流／卡住）— 現場處理，App 唔可以 bypass。',
    ec_limit: 'EC 目標超出上限。',
    microbial_limit: '微生物循環已達上限時數。',
    hardware_only: '此警報屬硬件級，App 唔可以遠端 reset。',
    max_houses: `已達上限 ${MAX_HOUSES} 個溫室。`,
    duplicate_code: '溫室編號已存在。',
  };
  const en = {
    viewer_readonly: 'Viewer role: status/3D only — controls disabled.',
    hardware_lock: 'Hardware interlock (E-stop / RCD / overload / watchdog) — not remotely clearable.',
    typhoon_lock: 'Typhoon/storm window lock — field unlock only; App cannot force-open.',
    wind_alarm_lock: 'Wind alarm: cannot open windows further.',
    wind_alarm_no_force_open: 'Wind alarm: force-open all windows blocked.',
    wind_alarm_curtain_safe: 'Wind alarm: curtain must stay in safe position.',
    low_water: 'Tank low: pumps disabled.',
    ac_interlock: 'A/C enable ON → exhaust forced OFF.',
    actuator_fault: 'Actuator fault — field service only.',
    ec_limit: 'EC target above max.',
    microbial_limit: 'Microbial cycle hit max hours.',
    hardware_only: 'Hardware-class alarm — no remote reset.',
    max_houses: `Max ${MAX_HOUSES} greenhouses.`,
    duplicate_code: 'House code already exists.',
  };
  const map = langArg === 'en' ? en : zh;
  return map[code] || code;
}

let started = false;
export function startMockRuntime() {
  if (started) return;
  started = true;
  setInterval(tick, 1000);
}
